export function generateSessionDates(year) {
  const dates = [];
  const start = new Date(year, 0, 1);
  const end   = new Date(year, 11, 31);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const day = d.getDay();
    if (day === 0 || day === 3)
      dates.push({ date: new Date(d), dayType: day === 3 ? 'wednesday' : 'sunday' });
  }
  return dates;
}

function buildPersonMap(persons) {
  const map = {};
  for (const p of persons) map[p.id] = p;
  return map;
}

function buildQueue(assignments, byType, byPersonMap, activeOnly = true) {
  return assignments
    .filter(a => a[byType[0]] === byType[1] && (!activeOnly || a.active !== false))
    .map(a => byPersonMap[a.personId])
    .filter(Boolean);
}

function pickTwo(queue, idx) {
  if (queue.length === 0) return [null, null, idx];
  const first = queue[idx % queue.length]; idx++;
  if (queue.length < 2) return [first, null, idx];
  const second = queue[idx % queue.length]; idx++;
  return [first, second, idx];
}

function pickTwoUshers(pool, busySet, idx) {
  if (pool.length === 0) return [[], idx];
  const picked = [];
  const seen   = new Set();
  let i = idx, attempts = 0;
  while (picked.length < 2 && attempts < pool.length * 3) {
    const p = pool[i % pool.length];
    if (!busySet.has(p.id) && !seen.has(p.id)) { picked.push(p); seen.add(p.id); }
    i++; attempts++;
  }
  return [picked, i];
}

// 슬롯 전용 → 일반 순으로 우선 배정, skipId와 겹치면 다음으로
function pickSlot(dedicatedQueue, generalQueue, dedicatedIdx, generalIdx, skipId) {
  if (dedicatedQueue.length > 0) {
    const p = dedicatedQueue[dedicatedIdx % dedicatedQueue.length];
    return [p, dedicatedIdx + 1, generalIdx];
  }
  if (generalQueue.length === 0) return [null, dedicatedIdx, generalIdx];
  let attempts = 0;
  let p;
  do {
    p = generalQueue[generalIdx % generalQueue.length]; generalIdx++;
    attempts++;
  } while (p?.id === skipId && attempts < generalQueue.length);
  if (p?.id === skipId) p = null;
  return [p, dedicatedIdx, generalIdx];
}

export function generateSchedule(facilitators, prayers, ushers, persons, year) {
  const byPerson = buildPersonMap(persons);
  const dates    = generateSessionDates(year);

  // 사회자 큐
  const wedFacs      = buildQueue(facilitators, ['dayType', 'wednesday'],      byPerson);
  const wedNoonFacs  = buildQueue(facilitators, ['dayType', 'wednesday-noon'], byPerson);
  const wedEveFacs   = buildQueue(facilitators, ['dayType', 'wednesday-eve'],  byPerson);
  const sunFacs      = buildQueue(facilitators, ['dayType', 'sunday'],         byPerson);

  // 기도자 큐
  const wedPrays      = buildQueue(prayers, ['dayType', 'wednesday'],      byPerson);
  const wed9amPrays   = buildQueue(prayers, ['dayType', 'wednesday-9am'], byPerson);
  const sun9amPrays   = buildQueue(prayers, ['dayType', 'sunday-9am'],    byPerson);
  const sunNoonPrays  = buildQueue(prayers, ['dayType', 'sunday-noon'],   byPerson);

  // 안내위원 풀
  const alwaysPool      = ushers.filter(u => u.usherType === 'always'         && u.active !== false).map(u => byPerson[u.personId]).filter(Boolean);
  const wedEveOnlyPool  = ushers.filter(u => u.usherType === 'wednesday-only' && u.active !== false).map(u => byPerson[u.personId]).filter(Boolean);
  const wedNoonOnlyPool = ushers.filter(u => u.usherType === 'wednesday-noon' && u.active !== false).map(u => byPerson[u.personId]).filter(Boolean);

  // 수요: 정오 풀 = 언제나 + 수요정오전용, 저녁 풀 = 언제나 + 수요저녁전용
  const wedNoonUsherPool = [...alwaysPool, ...wedNoonOnlyPool];
  const wedEveUsherPool  = [...alwaysPool, ...wedEveOnlyPool];
  const sunUsherPool     = alwaysPool;

  let wedFacIdx = 0, wedNoonFacIdx = 0, wedEveFacIdx = 0, sunFacIdx = 0;
  let wedPrayIdx = 0, wed9amPrayIdx = 0, sun9amPrayIdx = 0, sunNoonPrayIdx = 0;
  let wedNoonUsherIdx = 0, wedEveUsherIdx = 0, sunUsherIdx = 0;

  return dates.map(({ date, dayType }) => {
    let s1Fac, s2Fac, s1Pray, s2Pray;

    if (dayType === 'wednesday') {
      // 사회자
      [s1Fac, wedNoonFacIdx, wedFacIdx] = pickSlot(wedNoonFacs, wedFacs, wedNoonFacIdx, wedFacIdx, null);
      [s2Fac, wedEveFacIdx,  wedFacIdx] = pickSlot(wedEveFacs,  wedFacs, wedEveFacIdx,  wedFacIdx, s1Fac?.id);

      // 기도자: 정오(9시) 전용 → 일반, 저녁은 일반에서만
      [s1Pray, wed9amPrayIdx, wedPrayIdx] = pickSlot(wed9amPrays, wedPrays, wed9amPrayIdx, wedPrayIdx, null);
      if (wedPrays.length > 0) {
        let attempts = 0;
        do {
          s2Pray = wedPrays[wedPrayIdx % wedPrays.length]; wedPrayIdx++;
          attempts++;
        } while (s2Pray?.id === s1Pray?.id && attempts < wedPrays.length);
        if (s2Pray?.id === s1Pray?.id) s2Pray = null;
      } else {
        s2Pray = null;
      }

      // 안내: 정오/저녁 별도 배정
      const busyIds = new Set([s1Fac?.id, s2Fac?.id, s1Pray?.id, s2Pray?.id].filter(Boolean));
      const [noonUshers, nextNoonIdx] = pickTwoUshers(wedNoonUsherPool, busyIds, wedNoonUsherIdx);
      wedNoonUsherIdx = nextNoonIdx;
      const noonBusyIds = new Set([...busyIds, ...noonUshers.map(u => u.id)]);
      const [eveUshers,  nextEveIdx]  = pickTwoUshers(wedEveUsherPool,  noonBusyIds, wedEveUsherIdx);
      wedEveUsherIdx = nextEveIdx;

      return {
        date, dayType,
        noonFacilitator: s1Fac ?? null, eveningFacilitator: s2Fac ?? null,
        noonPrayer:      s1Pray ?? null, eveningPrayer:      s2Pray ?? null,
        noonUshers, eveUshers,
        ushers: [...noonUshers, ...eveUshers], // 통계/호환용
      };
    } else {
      [s1Fac, s2Fac, sunFacIdx] = pickTwo(sunFacs, sunFacIdx);
      s1Pray = sun9amPrays.length  ? sun9amPrays[sun9amPrayIdx   % sun9amPrays.length]  : null; sun9amPrayIdx++;
      s2Pray = sunNoonPrays.length ? sunNoonPrays[sunNoonPrayIdx % sunNoonPrays.length] : null; sunNoonPrayIdx++;

      const busyIds = new Set([s1Fac?.id, s2Fac?.id, s1Pray?.id, s2Pray?.id].filter(Boolean));
      const [dayUshers, nextIdx] = pickTwoUshers(sunUsherPool, busyIds, sunUsherIdx);
      sunUsherIdx = nextIdx;

      return {
        date, dayType,
        noonFacilitator: s1Fac ?? null, eveningFacilitator: s2Fac ?? null,
        noonPrayer:      s1Pray ?? null, eveningPrayer:      s2Pray ?? null,
        noonUshers: null, eveUshers: null,
        ushers: dayUshers,
      };
    }
  });
}

export function getSessionLabels(dayType) {
  return dayType === 'wednesday'
    ? { first: '정오', second: '저녁' }
    : { first: '9시',  second: '정오' };
}

export function groupScheduleByMonth(schedule) {
  const months = {};
  for (const s of schedule) {
    const m = s.date.getMonth();
    if (!months[m]) months[m] = [];
    months[m].push(s);
  }
  return months;
}

export const MONTH_NAMES = [
  '1월','2월','3월','4월','5월','6월',
  '7월','8월','9월','10월','11월','12월',
];
export const DAY_LABELS = { wednesday: '수요일', sunday: '일요일' };

export function formatDate(date) {
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

export function toDateKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export function applyOverrides(schedule, overrides, persons) {
  if (!overrides || Object.keys(overrides).length === 0) return schedule;
  const byId = {};
  for (const p of persons) byId[p.id] = p;

  return schedule.map(s => {
    const ov = overrides[toDateKey(s.date)];
    if (!ov) return s;
    const resolve  = id => (id ? (byId[id] ?? null) : null);
    const resolveArr = ids => ids ? ids.map(id => byId[id]).filter(Boolean) : null;

    const noonUshers = resolveArr(ov.noonUsherIds) ?? s.noonUshers;
    const eveUshers  = resolveArr(ov.eveUsherIds)  ?? s.eveUshers;

    return {
      ...s,
      noonFacilitator:    'noonFacilitatorId'   in ov ? resolve(ov.noonFacilitatorId)   : s.noonFacilitator,
      eveningFacilitator: 'eveningFacilitatorId' in ov ? resolve(ov.eveningFacilitatorId) : s.eveningFacilitator,
      noonPrayer:         'noonPrayerId'         in ov ? resolve(ov.noonPrayerId)         : s.noonPrayer,
      eveningPrayer:      'eveningPrayerId'      in ov ? resolve(ov.eveningPrayerId)      : s.eveningPrayer,
      noonUshers,
      eveUshers,
      ushers: s.dayType === 'wednesday'
        ? [...(noonUshers ?? []), ...(eveUshers ?? [])]
        : (resolveArr(ov.usherIds) ?? s.ushers),
      _overridden: true,
    };
  });
}
